import { memoWithWeakMap } from '@tencent/data-mapper';
import { makeEffectStub } from "../../lib/utils";
import { CSS_PRIORITY_HACK_PREFIX, PSEUDO_PREVIEW_BLACKLIST } from "../../lib/utils/consts";
import { compileSCSS, compileLayoutExtraStyle } from "../../lib/utils/styling";
import Vue from 'vue';
import { mapValues } from "lodash-es";
import { convertStyle } from "../../lib/ucBus/styleConvertPlugin";
export class StyleLoader {
  constructor(renderer, overrides = {}) {
    this.exisitngTags = new Map();
    this.disposers = [];
    this.renderer = renderer;
    this.uc = renderer.uc;
    this.options = {
      getPagelet: overrides.getPagelet || (() => renderer.ucPagelet),
      getVNodeFactory: overrides.getVNodeFactory || (() => renderer.ucVNodeFactory)
    };
  }
  init() {
    var _this$uc$designer;
    const {
      renderer,
      options
    } = this;
    const rendererUUID = this.renderer.ucRendererUUID;
    renderer.$once('hook:beforeDestroy', () => this.dispose());
    const use = (getter, user) => {
      this.disposers.push(renderer.$watch(getter, user, {
        immediate: true
      }));
    };
    // 公共样式表
    use(() => makePageletStyleSheetCSS(options.getPagelet()), css => this.applyStyle('<pagelet stylesheets>', css));
    // 全局样式
    const globalStyle = globalStyleLoader(this.uc);
    globalStyle.init();
    this.disposers.push(globalStyle.dispose);
    const eff = makeEffectStub(root => {
      if (!root) return;
      const x = rootFactoryStaticStyles(root);
      x.init(renderer);
      this.fLUT = x.fLUT;
      return () => x.unref();
    });
    use(() => options.getVNodeFactory(), eff);
    this.disposers.push(() => eff(null));
    // Design Mode
    const stylePanelState = (_this$uc$designer = this.uc.designer) === null || _this$uc$designer === void 0 ? void 0 : _this$uc$designer.stylePanelState;
    if (stylePanelState) {
      use(() => {
        var _this$uc$designer$ren, _style$selector;
        if (((_this$uc$designer$ren = this.uc.designer.renderer) === null || _this$uc$designer$ren === void 0 ? void 0 : _this$uc$designer$ren.ucRendererUUID) !== rendererUUID) return '';
        const {
          currentSelector: selector,
          currentPseudoClass: pseudoClass,
          currentPseudoClassInfo
        } = stylePanelState;
        if (!pseudoClass) return '';
        // 伪元素选择器通常来说不需要特殊的预览
        if (PSEUDO_PREVIEW_BLACKLIST.find(item => pseudoClass.startsWith(item))) return '';
        // 组件开发者可以决定伪类要不要预览 因为有时候会在伪类里写选择器
        if (currentPseudoClassInfo !== null && currentPseudoClassInfo !== void 0 && currentPseudoClassInfo.disablePreview) return '';
        // 切到hover并且[ext,"",&hover]为空的时候 change不会触发到这里 把styles补上依赖
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        stylePanelState.styles;
        const comp = this.fLUT.get(this.uc.designer.compPath);
        if (!comp) return '';
        const style = comp.dynamicStyleGetter ? comp.dynamicStyleGetter(this.renderer) : comp.style;
        const rules = style === null || style === void 0 || (_style$selector = style[selector]) === null || _style$selector === void 0 ? void 0 : _style$selector[pseudoClass];
        if (!rules) return '';
        const css = compileLayoutExtraStyle({
          [selector]: {
            '': rules
          }
        });
        if (!css) return '';
        const cssPrefix = `.${comp.styleKey}[data-uc-renderer-uuid="${rendererUUID}"] { ${CSS_PRIORITY_HACK_PREFIX} {\n`;
        const cssSuffix = '\n}}\n';
        return compileSCSS(cssPrefix + css + cssSuffix);
      }, css => {
        this.applyStyle('<pseudo class preview>', css);
      });
    }
  }
  /**
   * 创建 / 更新 / 删除一个 style 标签
   *
   * @param key 一般是 compPath
   * @param source CSS 源代码，直接插入 <style> 标签里面的
   */
  applyStyle(key, source) {
    const existing = this.exisitngTags.get(key);
    if (!existing) {
      if (!source) return;
      const el = makeStyleElementAndInsert(source, {
        'data-uc-key': key,
        'data-uc-renderer-uuid': this.renderer.ucRendererUUID
      });
      this.exisitngTags.set(key, {
        source,
        el
      });
      return;
    }
    if (!source) {
      existing.el.remove();
      this.exisitngTags.delete(key);
      return;
    }
    existing.el.textContent = source;
    existing.source = source;
  }
  dispose() {
    this.exisitngTags.forEach(it => it.el.remove());
    this.exisitngTags.clear();
    this.disposers.splice(0).forEach(f => f());
  }
}
const rootFactoryStaticStyles = memoWithWeakMap(root => {
  var _root$styledChildren;
  let el;
  const fLUT = new Map();
  const staticCSS = [];
  const dynInitializers = [];
  const uninitDyns = [];
  root === null || root === void 0 || (_root$styledChildren = root.styledChildren) === null || _root$styledChildren === void 0 || _root$styledChildren.forEach(factory => {
    const cssPrefix = `.${factory.styleKey} { ${CSS_PRIORITY_HACK_PREFIX} { \n`;
    const cssSuffix = '\n}}\n';
    fLUT.set(factory.compPath, factory);
    if (factory !== null && factory !== void 0 && factory.dynamicStyleGetter || factory !== null && factory !== void 0 && factory.dynamicCondStyleGetter) {
      // 动态的样式，要加 watcher
      dynInitializers.push(renderer => {
        const rendererUUID = renderer.ucRendererUUID;
        let el = makeStyleElementAndInsert('', {
          'data-uc-key': '<component dynamic style>',
          'data-uc-renderer-uuid': rendererUUID
        });
        const scopedCssPrefix = `.${factory.styleKey}[data-uc-renderer-uuid="${rendererUUID}"] { ${CSS_PRIORITY_HACK_PREFIX} { \n`;
        const shutdown = () => {
          if (!el) return;
          stopWatch();
          el.remove();
          el = undefined;
        };
        const stopWatch = renderer.$watch(() => {
          // 对于动态计算出来的样式，都需要 convertStyle 处理
          const convertStyleCustom = mixedStyle => {
            const {
              uc
            } = renderer;
            const ctxBase = {
              compPath: factory.compPath,
              comp: factory.comp,
              uc
            };
            return mapValues(mixedStyle, (v, selector) => mapValues(v, (v, pseudo) => convertStyle(uc, v, {
              ...ctxBase,
              selector,
              pseudo
            })));
          };
          const cssArr = [];
          if (factory !== null && factory !== void 0 && factory.dynamicStyleGetter) {
            cssArr.push(compileLayoutExtraStyle(convertStyleCustom(factory.dynamicStyleGetter(renderer))));
          }
          if (factory !== null && factory !== void 0 && factory.dynamicCondStyleGetter) {
            cssArr.push(compileLayoutExtraStyle(convertStyleCustom(factory.dynamicCondStyleGetter(renderer))));
          }
          return cssArr.join('\n');
        }, css => el && (el.textContent = !css ? '' : compileSCSS(scopedCssPrefix + css + cssSuffix)), {
          immediate: true
        });
        renderer.$on('hook:beforeDestroy', shutdown);
        uninitDyns.push(shutdown);
      });
    }
    if (!(factory !== null && factory !== void 0 && factory.dynamicStyleGetter)) {
      // 普通的样式，ExtraStyle没有花括号表达式，普通操作就行
      const css = compileLayoutExtraStyle(factory.style);
      if (css) staticCSS.push(compileSCSS(cssPrefix + css + cssSuffix));
    }
  });
  // ----------------------------------------------------------------
  if (staticCSS.length) {
    el = makeStyleElementAndInsert(staticCSS.join('\n\n'), {
      'data-uc-key': '<static component style>'
    });
    staticCSS.splice(0);
  }
  // ----------------------------------------------------------------
  let refCount = 0;
  return {
    fLUT,
    init(renderer) {
      refCount += 1;
      dynInitializers.forEach(fn => fn(renderer));
      if (refCount === 1) {
        el && document.head.appendChild(el);
      }
    },
    unref() {
      refCount -= 1;
      if (refCount === 0) {
        uninitDyns.splice(0).forEach(f => f());
        // 晚一点释放 因为新的renderuuid会在vuetick同步时才刷新到页面上
        Vue.nextTick(() => {
          var _el;
          return (_el = el) === null || _el === void 0 ? void 0 : _el.remove();
        });
      }
    }
  };
});
function makeStyleElementAndInsert(css, attrs) {
  const el = document.createElement('style');
  el.textContent = css;
  attrs && Object.keys(attrs).forEach(key => {
    el.setAttribute(key, attrs[key]);
  });
  document.head.appendChild(el);
  return el;
}
// 公共样式
function makePageletStyleSheetCSS(pagelet) {
  var _pagelet$styleSheets;
  if (!pagelet) return '';
  const ans = [];
  (_pagelet$styleSheets = pagelet.styleSheets) === null || _pagelet$styleSheets === void 0 || _pagelet$styleSheets.forEach(sheet => {
    ans.push(`.${sheet.id}{ ${CSS_PRIORITY_HACK_PREFIX} {\n${sheet.source}\n}}`);
  });
  if (!ans.length) return '';
  return compileSCSS(ans.join('\n'));
}
// 水光 - 全局样式
const globalStyleLoader = memoWithWeakMap(uc => {
  const el = makeStyleElementAndInsert('', {
    'data-uc-key': '<global style>'
  });
  let refCount = 0;
  let removeWatcher;
  let {
    isDesignMode
  } = uc;
  // 给xy的全局样式面板留的特殊接口
  if (uc.config.componentStylesForceDesignMode) {
    isDesignMode = true;
  }
  const update = styles => {
    const result = [];
    for (const [type, style] of Object.entries(styles)) {
      for (const [selector, val] of Object.entries(style)) {
        let sass;
        if (type === 'w-container') {
          // 页面的根组件特殊处理
          sass = compileLayoutExtraStyle({
            [selector]: val
          });
          const prefix = isDesignMode ? '.ucd-editZone-renderer, .uc-renderer' : ':root > body';
          result.push(`${prefix} {\n${sass}\n}`);
        } else {
          const cssSel = `[data-uc-type="${type}"] ${selector}`;
          const source = compileLayoutExtraStyle({
            [cssSel]: val
          });
          // 页面片和弹窗的根组件的选择器需要特殊处理
          if (type === 'xy-pagelet-container' || type === 'xy-modal-container') {
            sass = source;
          } else {
            sass = `${CSS_PRIORITY_HACK_PREFIX} {\n${source}\n}`;
          }
        }
        result.push(sass);
      }
    }
    const css = compileSCSS(result.join('\n'));
    el.textContent = css;
  };
  return {
    dispose: () => {
      refCount -= 1;
      if (refCount === 0) {
        removeWatcher === null || removeWatcher === void 0 || removeWatcher();
        el.remove();
      }
    },
    init: () => {
      refCount += 1;
      if (refCount === 1) {
        uc.$watch(() => uc.config.componentStyles, styles => update(styles !== null && styles !== void 0 ? styles : {}), {
          immediate: true,
          deep: true
        });
      }
    }
  };
});